import React, { useEffect, useRef, useState, useMemo } from "react";
import {
    Box, CssBaseline, Breadcrumbs, Link, Typography, Stack, Button,
    Paper, TextField, InputAdornment, IconButton, TableContainer,
    Table, TableHead, TableRow, TableCell, TableBody, TableSortLabel,
    CircularProgress, Tooltip, Chip, Dialog, DialogTitle, DialogContent,
    DialogActions, Snackbar, Alert, Avatar, TablePagination, Autocomplete
} from "@mui/material";
import {
    Search, Add, DeleteOutline, WarningAmber,
    NavigateNext, Download, PersonAdd, School,
    FilterList
} from '@mui/icons-material';
import { useNavigate, useParams } from "react-router-dom";
import { saveAs } from 'file-saver';
import { pdf } from '@react-pdf/renderer';
import StudentListPDF from "./studentList"; // Assuming this file exists as per your import
import Header from "../../component/header/Header";

const colors = {
    pageBg: '#F8F9FA',
    sidebarBg: '#F1F2F6',
    filterBg: "#FFF9E6",
    accent: '#FFC107',
    accentDark: '#FFA000',
    tableHeader: '#FDF7E2',
    textMain: '#5D4037',
    border: '#F0E6CC'
};

const CourseStudentListScreen = () => {
    const { id, classname } = useParams();
    const navigation = useNavigate();

    // --- State ---
    const [studentList, setStudentList] = useState([]);
    const [allStudentList, setAllStudentList] = useState([]);
    const [loading, setLoading] = useState(false);

    // Pagination & Sorting
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('fullname');
    const [filterSearch, setFilterSearch] = useState("");

    // Dialog & Actions State
    const [openAddDialog, setOpenAddDialog] = useState(false);
    const [loadingAdd, setLoadingAdd] = useState(false);
    const [selectedOptions, setSelectedOptions] = useState([]); // For Autocomplete UI
    const [selectedStudentIds, setSelectedStudentIds] = useState([]); // For API payload

    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [tempDeleteData, setTempDeleteData] = useState(null);
    const [loadingDelete, setLoadingDelete] = useState(false);

    // Feedback
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    useEffect(() => {
        getStudentList();
        localStorage.setItem("current_tab", "Class");
    }, []);

    // --- APIs ---

    const getStudentList = async () => {
        setLoading(true);
        try {
            const response = await fetch(`https://www.geniusmileniaeducation.link/db-api/students-in-course/${id}`);
            const resJson = await response.json();
            if (resJson.status === 1) {
                setStudentList(resJson.data || []);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const getAllStudentList = async () => {
        try {
            const response = await fetch(`https://www.geniusmileniaeducation.link/db-api/students`);
            const resJson = await response.json();
            if (resJson.status === 1) {
                setAllStudentList(resJson.data || []);
            }
        } catch (error) {
            setSnackbar({ open: true, message: "Failed to load student directory", severity: "error" });
        }
    };

    const addStudent = async () => {
        if (selectedStudentIds.length === 0) return;
        setLoadingAdd(true);
        try {
            const response = await fetch(`https://www.geniusmileniaeducation.link/db-api/courses/${id}/enroll`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    enrollmentDate: new Date(),
                    studentIds: selectedStudentIds
                })
            });
            const resJson = await response.json();
            if (resJson.status === 1) {
                setSnackbar({ open: true, message: "Students enrolled successfully!", severity: "success" });
                setOpenAddDialog(false);
                setSelectedOptions([]);
                setSelectedStudentIds([]);
                getStudentList();
            } else {
                setSnackbar({ open: true, message: resJson.message, severity: "error" });
            }
        } catch (error) {
            setSnackbar({ open: true, message: "Enrollment failed", severity: "error" });
        } finally {
            setLoadingAdd(false);
        }
    };

    const deleteStudent = async (studentCourseId) => {
        setLoadingDelete(true);
        try {
            // Note: Ensuring this endpoint removes from class, not deletes user entirely, based on context
            const response = await fetch(`https://www.geniusmileniaeducation.link/db-api/students-in-course/${studentCourseId}`, {
                method: 'DELETE'
            });
            const resJson = await response.json();
            if (resJson.status === 1) {
                setSnackbar({ open: true, message: "Student removed successfully", severity: "success" });
                setOpenDeleteDialog(false);
                getStudentList();
            } else {
                setSnackbar({ open: true, message: resJson.message, severity: "error" });
            }
        } catch (error) {
            setSnackbar({ open: true, message: "Delete failed", severity: "error" });
        } finally {
            setLoadingDelete(false);
        }
    };

    const handleDownload = async () => {
        try {
            const blob = await pdf(
                <StudentListPDF classData={classname} studentData={studentList} />
            ).toBlob();
            saveAs(blob, `${classname}_StudentList.pdf`);
        } catch (error) {
            setSnackbar({ open: true, message: "PDF Generation failed", severity: "error" });
        }
    };

    // --- Table Helpers ---

    const handleSortRequest = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const filteredAndSortedData = useMemo(() => {
        return studentList
            .filter((item) => item.fullname?.toUpperCase().includes(filterSearch.toUpperCase()))
            .sort((a, b) => {
                const aValue = a[orderBy] || "";
                const bValue = b[orderBy] || "";
                return order === 'asc' ? (aValue < bValue ? -1 : 1) : (bValue < aValue ? -1 : 1);
            });
    }, [studentList, filterSearch, order, orderBy]);

    // Generate color based on name
    const stringToColor = (string) => {
        let hash = 0;
        for (let i = 0; i < string.length; i++) {
            hash = string.charCodeAt(i) + ((hash << 5) - hash);
        }
        let color = '#';
        for (let i = 0; i < 3; i++) {
            const value = (hash >> (i * 8)) & 0xff;
            color += `00${value.toString(16)}`.slice(-2);
        }
        return color;
    };

    return (
        <Box sx={{ display: 'flex', bgcolor: colors.sidebarBg, minHeight: '100vh' }}>
            <CssBaseline />
            <Box component="main" sx={{ flexGrow: 1, p: 3, bgcolor: "white", m: 2, borderRadius: 5, border: `1px solid ${colors.border}` }}>

                {/* Breadcrumbs */}
                <Breadcrumbs separator={<NavigateNext fontSize="small" />} sx={{ mb: 2 }}>
                    <Link underline="hover" color="inherit" onClick={() => navigation("/Portal")} sx={{ cursor: 'pointer' }}>Portal</Link>
                    <Link underline="hover" color="inherit" onClick={() => navigation("/Portal/Class")} sx={{ cursor: 'pointer' }}>Class</Link>
                    <Typography color="text.primary">Student List</Typography>
                </Breadcrumbs>

                <Header title={classname} subtitle="Manage students enrolled in this class" />

                {/* Toolbar */}
                <Paper elevation={0} sx={{ p: 2, my: 3, bgcolor: colors.filterBg, borderRadius: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <FilterList sx={{ color: colors.textMain }} />
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Search student name..."
                        value={filterSearch}
                        onChange={(e) => { setFilterSearch(e.target.value); setPage(0); }}
                        sx={{ bgcolor: 'white', borderRadius: 2, maxWidth: 400, flexGrow: 1 }}
                        InputProps={{
                            startAdornment: (<InputAdornment position="start"><Search fontSize="small" /></InputAdornment>)
                        }}
                    />
                    <Stack direction="row" spacing={2}>
                        <Button
                            variant="outlined"
                            startIcon={<Download />}
                            onClick={handleDownload}
                            sx={{ bgcolor: 'white', color: colors.textMain, borderColor: colors.border, '&:hover': { borderColor: colors.accent } }}
                        >
                            Download PDF
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<PersonAdd />}
                            onClick={() => { getAllStudentList(); setOpenAddDialog(true); }}
                            sx={{ bgcolor: colors.accent, color: 'black', '&:hover': { bgcolor: colors.accentDark } }}
                        >
                            Add Student
                        </Button>
                    </Stack>
                </Paper>

                {/* Table */}
                <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${colors.border}`, borderRadius: 3 }}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ bgcolor: colors.tableHeader, fontWeight: 'bold', color: colors.textMain }}>
                                    <TableSortLabel
                                        active={orderBy === 'fullname'}
                                        direction={orderBy === 'fullname' ? order : 'asc'}
                                        onClick={() => handleSortRequest('fullname')}
                                    >
                                        Student Name
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ bgcolor: colors.tableHeader, fontWeight: 'bold', color: colors.textMain }}>Status</TableCell>
                                <TableCell align="center" sx={{ bgcolor: colors.tableHeader, fontWeight: 'bold', color: colors.textMain }}>Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow><TableCell colSpan={3} align="center" sx={{ py: 3 }}><CircularProgress size={30} /></TableCell></TableRow>
                            ) : filteredAndSortedData.length === 0 ? (
                                <TableRow><TableCell colSpan={3} align="center" sx={{ py: 3, color: 'text.secondary' }}>No students found in this class.</TableCell></TableRow>
                            ) : filteredAndSortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
                                <TableRow key={row.studentId} hover>
                                    <TableCell>
                                        <Stack direction="row" spacing={2} alignItems="center">
                                            <Avatar
                                                sx={{
                                                    width: 32, height: 32, fontSize: 14,
                                                    bgcolor: stringToColor(row.fullname || ""),
                                                    color: 'white'
                                                }}
                                            >
                                                {row.fullname?.charAt(0)}
                                            </Avatar>
                                            <Typography variant="body2" fontWeight={500}>{row.fullname}</Typography>
                                        </Stack>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={row.status === "A" ? "Active" : "Inactive"}
                                            size="small"
                                            color={row.status === "A" ? "success" : "default"}
                                            sx={{ borderRadius: 1.5, fontWeight: 'bold' }}
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Tooltip title="Remove from Class">
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => { setTempDeleteData(row); setOpenDeleteDialog(true); }}
                                            >
                                                <DeleteOutline />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    rowsPerPageOptions={[10, 25, 50]}
                    component="div"
                    count={filteredAndSortedData.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                />

                {/* --- ADD STUDENT DIALOG --- */}
                <Dialog open={openAddDialog} onClose={() => setOpenAddDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                    <DialogTitle sx={{ fontWeight: 'bold', bgcolor: colors.pageBg, borderBottom: `1px solid ${colors.border}` }}>
                        Enroll Student
                    </DialogTitle>
                    <DialogContent sx={{ mt: 2, minHeight: 200 }}>
                        <Stack spacing={2} pt={1}>
                            <Typography variant="body2" color="text.secondary">Search for students to add to <b>{classname}</b>:</Typography>
                            <Autocomplete
                                multiple
                                options={allStudentList}
                                getOptionLabel={(option) => option.fullname}
                                value={selectedOptions}
                                onChange={(event, newValue) => {
                                    setSelectedOptions(newValue);
                                    setSelectedStudentIds(newValue.map(val => val.studentId));
                                }}
                                isOptionEqualToValue={(option, value) => option.studentId === value.studentId}
                                renderInput={(params) => (
                                    <TextField {...params} label="Select Students" placeholder="Search by name..." />
                                )}
                                renderTags={(value, getTagProps) =>
                                    value.map((option, index) => (
                                        <Chip key={option.studentId} label={option.fullname} {...getTagProps({ index })} size="small" />
                                    ))
                                }
                            />
                        </Stack>
                    </DialogContent>
                    <DialogActions sx={{ p: 2, bgcolor: colors.pageBg, borderTop: `1px solid ${colors.border}` }}>
                        <Button onClick={() => setOpenAddDialog(false)} color="inherit">Cancel</Button>
                        <Button
                            variant="contained"
                            onClick={addStudent}
                            disabled={loadingAdd || selectedOptions.length === 0}
                            sx={{ bgcolor: colors.accent, color: 'blank', '&:hover': { bgcolor: colors.accentDark } }}
                        >
                            {loadingAdd ? <CircularProgress size={20} color="inherit" /> : "Enroll Students"}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* --- DELETE DIALOG --- */}
                <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)} PaperProps={{ sx: { borderRadius: 4 } }}>
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                        <WarningAmber sx={{ fontSize: 60, color: 'error.main', mb: 2 }} />
                        <DialogTitle sx={{ fontWeight: 'bold', p: 0 }}>Remove Student?</DialogTitle>
                        <DialogContent sx={{ mt: 1 }}>
                            <Typography>Are you sure you want to remove <b>{tempDeleteData?.fullname}</b> from this class?</Typography>
                        </DialogContent>
                        <DialogActions sx={{ justifyContent: 'center', gap: 2, mt: 1 }}>
                            <Button onClick={() => setOpenDeleteDialog(false)} color="inherit" variant="outlined" sx={{ borderRadius: 2 }}>Cancel</Button>
                            <Button
                                variant="contained"
                                color="error"
                                onClick={() => deleteStudent(tempDeleteData.studentCourseId)}
                                disabled={loadingDelete}
                                sx={{ borderRadius: 2, fontWeight: 'bold' }}
                            >
                                {loadingDelete ? <CircularProgress size={20} /> : "Confirm Remove"}
                            </Button>
                        </DialogActions>
                    </Box>
                </Dialog>

                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={4000}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                >
                    <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: 2 }}>
                        {snackbar.message}
                    </Alert>
                </Snackbar>

            </Box>
        </Box>
    );
};

export default CourseStudentListScreen;